import { Box, styled, Typography } from '@mui/material'

export const ColumnSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

export const ColumnSectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

export interface IColumnCardProps {
  isDragOver: boolean
  isEditing: boolean
}

export const ColumnCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'isDragOver' && prop !== 'isEditing',
})<IColumnCardProps>(({ theme, isDragOver, isEditing }) => ({
  border: `1px solid ${
    isDragOver ? theme.palette.primary.main : isEditing ? theme.palette.primary.light : theme.palette.divider
  }`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.25),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  backgroundColor: isDragOver
    ? theme.palette.primary.light + '18'
    : isEditing
    ? theme.palette.background.paper
    : theme.palette.grey[50],
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  cursor: isEditing ? 'default' : 'grab',
  '&:active': {
    cursor: isEditing ? 'default' : 'grabbing',
  },
}))

export const ColumnCardHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 32,
})

export const ColumnHeaderLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
  flex: 1,
})

export interface IColumnNameTextProps {
  isPlaceholder: boolean
}

export const ColumnNameText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'isPlaceholder',
})<IColumnNameTextProps>(({ theme, isPlaceholder }) => ({
  fontSize: '0.8rem',
  fontWeight: isPlaceholder ? 400 : 500,
  color: isPlaceholder ? theme.palette.text.disabled : theme.palette.text.primary,
  fontStyle: isPlaceholder ? 'italic' : 'normal',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}))

export const ColumnHeaderActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
})

export const ColumnEditBody = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  borderTop: `1px dashed ${theme.palette.divider}`,
}))

export const ColumnDragHandle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.disabled,
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing',
  },
}))

export const ColumnFieldRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}))

export const RequiredAsterisk = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  marginLeft: theme.spacing(0.25),
}))

export const ColumnFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.7rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}))

export const PlaceholderText = styled('span')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}))

export const EmptyColumnsHint = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.text.secondary,
  textAlign: 'center',
  padding: theme.spacing(1.5, 0),
  fontStyle: 'italic',
}))
